/**
 * Face Detection Controller
 *
 * Manages the face detection screen flow including:
 * - Countdown timer for capture
 * - Face quality checks
 * - First selfie capture
 * - Integration with lighting controller
 */

import { verificationState } from '../state/verification-state';
import { captureVideoFrameById } from '../utils/image-utils';
import { i18n } from '../../i18n';

/**
 * Face quality assessment result.
 */
export interface FaceQuality {
  faceTooSmall: boolean;
  faceTooLarge: boolean;
  faceInCenter: boolean;
  overall: 'good' | 'poor';
}

/**
 * Configuration for face detection controller.
 */
export interface FaceDetectionConfig {
  /** Countdown duration in seconds. Default: 3 */
  countdownSeconds: number;
  /** Video element ID to capture from. Default: 'camera-feed' */
  videoElementId: string;
  /** Interval for countdown updates in ms. Default: 1000 */
  countdownIntervalMs: number;
}

/**
 * Events emitted by the controller.
 */
export interface FaceDetectionEvents {
  onCountdownStart?: () => void;
  onCountdownTick?: (remaining: number) => void;
  onCountdownCancel?: () => void;
  onCapture?: (selfieDataUrl: string) => void;
  onStatusChange?: (message: string, instruction: string) => void;
  onFacePositioned?: () => void;
}

/**
 * Default configuration values.
 */
const DEFAULT_CONFIG: FaceDetectionConfig = {
  countdownSeconds: 3,
  videoElementId: 'camera-feed',
  countdownIntervalMs: 1000,
};

/**
 * FaceDetectionController manages the face detection screen.
 *
 * Features:
 * - 3-2-1 countdown when face is properly positioned
 * - Automatic capture at countdown completion
 * - Face quality checks (size, centering)
 * - Blocks progress during poor lighting
 * - Demo mode simulation
 */
export class FaceDetectionController {
  private config: FaceDetectionConfig;
  private events: FaceDetectionEvents;

  // Countdown state
  private countdownTimer: ReturnType<typeof setInterval> | null = null;
  private countdownValue = 0;
  private isCountingDown = false;

  // Detection state
  private isFaceDetected = false;
  private faceQuality: FaceQuality | null = null;
  private capturedSelfie: string | null = null;

  // Screen validation callback
  private isActiveScreen: () => boolean = () => true;
  private isLightingAcceptable: () => boolean = () => true;

  constructor(config: Partial<FaceDetectionConfig> = {}, events: FaceDetectionEvents = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.events = events;
  }

  /**
   * Sets a callback to check if we're still on the face detection screen.
   */
  setActiveScreenCheck(check: () => boolean): void {
    this.isActiveScreen = check;
  }

  /**
   * Sets a callback to check if lighting is acceptable.
   */
  setLightingCheck(check: () => boolean): void {
    this.isLightingAcceptable = check;
  }

  /**
   * Starts the countdown timer.
   * Only starts if face is detected, lighting is good, and not already counting.
   *
   * @returns true if countdown started, false otherwise
   */
  startCountdown(): boolean {
    // Only start countdown if we're on the face detection screen
    if (!this.isActiveScreen()) {
      return false;
    }

    // Already counting down
    if (this.isCountingDown) {
      return false;
    }

    // Block countdown if lighting is poor
    if (!this.isLightingAcceptable()) {
      console.log('[FaceDetection] Countdown blocked - poor lighting');
      this.events.onStatusChange?.(
        i18n.t('liveness.improveLighting.title', 'Improve lighting'),
        i18n.t('liveness.improveLighting.hint', 'Move to a brighter area')
      );
      return false;
    }

    this.isCountingDown = true;
    this.countdownValue = this.config.countdownSeconds;

    console.log('[FaceDetection] Starting countdown from', this.countdownValue);

    // Emit start event
    this.events.onCountdownStart?.();
    this.events.onStatusChange?.(
      i18n
        .t('face.holdStillCountdown', 'Hold still... {count}')
        .replace('{count}', String(this.countdownValue)),
      i18n.t('face.keepInPosition', 'Keep your face in position')
    );

    // Start countdown interval
    this.countdownTimer = setInterval(() => {
      // Double-check we're still on the right screen
      if (!this.isActiveScreen()) {
        this.cancelCountdown();
        return;
      }

      this.countdownValue--;
      this.events.onCountdownTick?.(this.countdownValue);

      if (this.countdownValue > 0) {
        this.events.onStatusChange?.(
          i18n
            .t('face.holdStillCountdown', 'Hold still... {count}')
            .replace('{count}', String(this.countdownValue)),
          i18n.t('face.keepInPosition', 'Keep your face in position')
        );
      } else {
        // Countdown complete - capture selfie
        this.capturedSelfie = captureVideoFrameById(this.config.videoElementId);
        console.log('[FaceDetection] Captured selfie at countdown=0:', !!this.capturedSelfie);

        // Update state
        verificationState.update('face', {
          selfieImage: this.capturedSelfie,
        });

        this.cancelCountdown();
        this.events.onStatusChange?.(i18n.t('face.perfect', 'Perfect!'), '');

        if (this.capturedSelfie) {
          this.events.onCapture?.(this.capturedSelfie);
        }
      }
    }, this.config.countdownIntervalMs);

    return true;
  }

  /**
   * Cancels the current countdown.
   */
  cancelCountdown(): void {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }

    const wasCountingDown = this.isCountingDown;
    this.isCountingDown = false;
    this.countdownValue = 0;

    if (wasCountingDown) {
      this.events.onCountdownCancel?.();
    }
  }

  /**
   * Updates face detection status.
   * Called from the face detector on each frame.
   *
   * @param detected - Whether a face is detected
   * @param quality - Face quality assessment
   */
  updateStatus(detected: boolean, quality: FaceQuality | null = null): void {
    this.isFaceDetected = detected;
    this.faceQuality = quality;

    // Only process if we're on the face detection screen
    if (!this.isActiveScreen()) {
      this.cancelCountdown();
      return;
    }

    // Update verification state
    verificationState.update('face', {
      detected,
      quality: quality?.overall === 'good' ? 1.0 : 0.5,
    });

    if (!detected) {
      this.cancelCountdown();
      this.events.onStatusChange?.(
        i18n.t('face.looking', 'No face detected'),
        i18n.t('face.centerInFrame', 'Place your head within the frame')
      );
      return;
    }

    // Face detected - check quality
    if (quality) {
      if (quality.faceTooSmall) {
        this.cancelCountdown();
        this.events.onStatusChange?.(
          i18n.t('face.moveCloser', 'Face too far'),
          i18n.t('face.tooFar', 'Face too far')
        );
        return;
      }
      if (quality.faceTooLarge) {
        this.cancelCountdown();
        this.events.onStatusChange?.(
          i18n.t('face.moveBack', 'Face too close'),
          i18n.t('face.tooClose', 'Face too close')
        );
        return;
      }
      if (!quality.faceInCenter) {
        this.cancelCountdown();
        this.events.onStatusChange?.(
          i18n.t('face.centerFace', 'Face off-center'),
          i18n.t('face.offCenter', 'Face off-center')
        );
        return;
      }
    }

    // Face is positioned correctly
    this.events.onFacePositioned?.();

    // Start countdown if not already running
    if (!this.isCountingDown) {
      this.startCountdown();
    }
  }

  /**
   * Gets the captured selfie image.
   */
  getCapturedSelfie(): string | null {
    return this.capturedSelfie;
  }

  /**
   * Checks if a face is currently detected.
   */
  isFaceCurrentlyDetected(): boolean {
    return this.isFaceDetected;
  }

  /**
   * Checks if countdown is currently active.
   */
  isCountdownActive(): boolean {
    return this.isCountingDown;
  }

  /**
   * Gets current countdown value.
   */
  getCurrentCountdownValue(): number {
    return this.countdownValue;
  }

  /**
   * Runs demo mode simulation.
   * Simulates face detection after a delay.
   *
   * @param onComplete - Callback when demo advances to next screen
   */
  runDemoSimulation(onComplete?: () => void): void {
    console.log('[FaceDetection] Running demo simulation...');

    this.events.onStatusChange?.(
      i18n.t('face.looking', 'Looking for face...'),
      i18n.t('face.centerInFrame', 'Place your head within the frame')
    );

    // After 1.5 seconds, simulate face detected
    setTimeout(() => {
      // Check if still on face detection screen
      if (!this.isActiveScreen()) {
        console.log('[FaceDetection] Demo cancelled - left screen');
        return;
      }

      console.log('[FaceDetection] Demo: Face detected');
      this.isFaceDetected = true;

      // Update state
      verificationState.update('face', {
        detected: true,
        quality: 1.0,
      });

      this.events.onFacePositioned?.();

      // Start the countdown
      const originalOnCapture = this.events.onCapture;
      this.events.onCapture = (selfie) => {
        originalOnCapture?.(selfie);
        onComplete?.();
        // Restore original
        this.events.onCapture = originalOnCapture;
      };

      this.startCountdown();
    }, 1500);
  }

  /**
   * Resets the controller state.
   */
  reset(): void {
    this.cancelCountdown();
    this.isFaceDetected = false;
    this.faceQuality = null;
    this.capturedSelfie = null;
  }

  /**
   * Disposes of the controller.
   */
  dispose(): void {
    this.cancelCountdown();
  }
}

/**
 * Singleton instance for convenience.
 */
export const faceDetectionController = new FaceDetectionController();
